import { Button, Card, Divider, Flex } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

// components
import BackButton from 'components/backButton/backButton';
import PageTitle from 'components/title/title';
import NotificationService from 'core/services/notification';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import { isRTL } from 'core/services/language';
import { onAddMeetingDecisionComments, onGetMeetingDecisions } from '../services/meetingServices';
import { DecisionComments, BoardMeetingDecision } from '../models/meetings';
import TextArea from 'antd/es/input/TextArea';

// services

// styles

// models

interface MeetingDecisionCommentsPageProps {}

const MeetingDecisionCommentsPage: FunctionComponent<MeetingDecisionCommentsPageProps> = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { id } = useParams();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [decisions, setDecisions] = useState<BoardMeetingDecision[]>([]);
	const [comments, setComments] = useState<DecisionComments[]>([]);
	const [commentsFooter, setCommentsFooter] = useState<string>('');

	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

	useEffect(() => {
		getDecisions();
	}, []);

	const getDecisions = async () => {
		try {
			setIsLoading(true);

			const response = await onGetMeetingDecisions(id as string);
			setDecisions(response.decisions);
			setComments(response.decisions.map((decision) => ({ decision_id: decision.id, board_comment: decision.board_comment || '' })));
			setCommentsFooter(response.decisions_comments_footer || '');

			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('ACTIONS.FETCH_DECISION_ERROR'));
			navigate(`/dashboard/actions/boards/meetings/view/${id}`);
		}
	};

	const updateComments = (decisionId: number, value: string) => {
		const updatedComments = [...comments];

		const index = updatedComments.findIndex((comment) => comment.decision_id === decisionId);
		if (index !== -1) updatedComments[index].board_comment = value;
		else updatedComments.push({ decision_id: decisionId, board_comment: value });

		setComments(updatedComments);
	};

	const handleSubmit = async () => {
		try {
			setIsSubmitting(true);

			await onAddMeetingDecisionComments(id as string, comments, commentsFooter);

			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.COMMENTS_ADDED_SUCCESS'));
			navigate(`/dashboard/actions/boards/meetings/view/${id}`);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.SUBMIT_ERROR'));
			setIsSubmitting(false);
		}
	};

	return (
		<>
			<PageTitle name={t('PAGES.MEETING_DECISION_COMMENTS')} />

			<BackButton />

			<Card>
				<h5>{t('ACTIONS.MEETING_DECISION_COMMENTS')}</h5>

				{isLoading ? (
					<LoadingComponent />
				) : (
					<>
						{decisions &&
							decisions.map((decision, index) => (
								<div key={index}>
									<div>
										<h6 className="mt-4 mb-2">
											{index + 1} - {isRTL() ? decision.translated_title.ar : (decision.translated_title.en ?? decision.translated_title.ar)}
										</h6>

										<TextArea
											value={comments.find((comment) => comment.decision_id === decision.id)?.board_comment}
											onChange={(e) => updateComments(decision.id, e.target.value)}
											autoSize={{ minRows: 3, maxRows: 5 }}
										/>
									</div>

									{index < decisions.length - 1 && <Divider />}
								</div>
							))}

						<Divider />

						<div>
							<h6 className="mt-4 mb-2">{t('ACTIONS.EXPLANATION')}</h6>

							<TextArea value={commentsFooter} onChange={(e) => setCommentsFooter(e.target.value)} autoSize={{ minRows: 3, maxRows: 5 }} />
						</div>

						<Flex justify="flex-end" align="center" gap={10} className="mt-4">
							<Button type="link" size="middle" danger onClick={() => navigate(`/dashboard/actions/boards/meetings/view/${id}`)}>
								{t('COMMON.CANCEL')}
							</Button>

							<Button type="primary" size="middle" loading={isSubmitting} disabled={isSubmitting} onClick={handleSubmit}>
								{t('COMMON.UPDATE')}
							</Button>
						</Flex>
					</>
				)}
			</Card>
		</>
	);
};

export default MeetingDecisionCommentsPage;
